import React, { Suspense } from "react";
import {
  Alert,
  Container,
  Grid,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OrderIdContext, StageContext } from "../../../contexes/OrderIdContext";
import { DiffDisplayGrid } from "./components/DiffDisplayGrid";
import VersionSelectionControls from "./components/VersionSelectionControls";
import { UsecaseDestinationSelects } from "./components/UsecaseDestinationSelects";
import { UsecaseDifferencesAlert } from "./components/UsecaseDifferencesAlert";
import { useCrossStageData } from "./hooks/useCrossStageData";
import { useUsecaseDifferences } from "./hooks/useUsecaseDifferences";
import { useComparison } from "./hooks/useComparison";
import { useUsecaseState } from "./hooks/useUsecaseState";
import {
  createCrossStageVersionOptions,
  createUsecaseOptions,
  createVersionOptions,
  createProductOptions,
  filterCbesByProduct,
  getAvailableDestinations,
} from "./utils";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../../../types/documents";
import { AccountingEventsGroup } from "../../../types/events";
import { CanonicalBusinessEvent } from "../../../types/cbes";
import {
  isAccountingEventsGroup,
  isCanonicalBusinessEvent,
} from "../../../helpers/typeChecks";
import { determineFlowTypeFromObjects } from "../../../helpers/flowTypeUtils";
import {
  DESTINATION_OPTIONS,
  DIFF_UNCHANGED_LINES_CONFIG,
  STAGE_OPTIONS,
} from "../../../constants";
import "json-diff-kit/dist/viewer.css";
import "json-diff-kit/viewer-monokai.css";
import { filterNonNoOpCBEs } from "../../../helpers/cbeUtils";
import { isDevo } from "../../../helpers/stageUtils";
import {
  CbeComparisonGroup,
  filterCbesByComparisonGroup,
} from "../../../helpers/cbeComparisonUtils";

type JsonDataType =
  | CanonicalBusinessEvent
  | AccountingEventsGroup
  | DigitalBusinessDocument
  | DigitalCostDocument
  | DigitalPaymentDocument;

export interface JsonCompareComponentProps {
  isModalVisible: boolean;
  objects: JsonDataType[];
  allCbes?: CanonicalBusinessEvent[];
  allProductIds?: string[];
}

const JsonCompareComponent: React.FC<JsonCompareComponentProps> = ({
  isModalVisible,
  objects: rawObjects,
  allCbes,
  allProductIds = [],
}) => {
  const currentStage = React.useContext(StageContext);
  const identifier = React.useContext(OrderIdContext);

  const initialParams = React.useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const hasUrlVersions = React.useRef(
    initialParams.has("cmpLeft") || initialParams.has("cmpRight")
  );

  const [selectedUsecase, setSelectedUsecase] =
    React.useState<CbeComparisonGroup | null>(null);
  const initialUsecaseValue = React.useRef(initialParams.get("cmpUsecase"));
  const [selectedDestination, setSelectedDestination] =
    React.useState<SelectProps.Option>(() => {
      const v = initialParams.get("cmpDest");
      const match = DESTINATION_OPTIONS.find((o) => o.value === v);
      return match || DESTINATION_OPTIONS[0];
    });
  const [selectedProduct, setSelectedProduct] =
    React.useState<SelectProps.Option | null>(() => {
      const v = initialParams.get("cmpProduct");
      return v ? { label: v, value: v } : null;
    });
  const [compareStage, setCompareStage] = React.useState(() => {
    const v = initialParams.get("cmpStage");
    return (v as typeof currentStage) || currentStage;
  });
  const [overrideId, setOverrideId] = React.useState("");
  const [diffUnchangedLinesOptions, setDiffUnchangedLinesOptions] =
    React.useState<boolean | object>(true);
  const [leftSelectedVersion, setLeftSelectedVersion] =
    React.useState<SelectProps.Option>({ label: "No version", value: "0" });
  const [rightSelectedVersion, setRightSelectedVersion] =
    React.useState<SelectProps.Option>({ label: "No version", value: "0" });

  const syncCompareParams = React.useCallback(
    (updates: Record<string, string | null>) => {
      const url = new URL(window.location.href);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null) url.searchParams.delete(k);
        else url.searchParams.set(k, v);
      }
      window.history.replaceState(null, "", url.toString());
    },
    []
  );

  const crossStageEnabled = compareStage !== currentStage;
  const isAccountingGroup =
    rawObjects.length > 0 && isAccountingEventsGroup(rawObjects[0]);
  const isCbe =
    rawObjects.length > 0 && isCanonicalBusinessEvent(rawObjects[0]);
  const flowType = determineFlowTypeFromObjects(rawObjects);

  const filteredData = React.useMemo(() => {
    return isCbe
      ? filterNonNoOpCBEs(rawObjects as CanonicalBusinessEvent[])
      : rawObjects;
  }, [rawObjects, isCbe]);

  const { crossStageData, crossStageRawData, crossStageLoading } =
    useCrossStageData(
      crossStageEnabled,
      compareStage,
      currentStage,
      overrideId,
      identifier,
      flowType
    );

  const usecaseOptions = React.useMemo(() => {
    if (!isCbe) return [];

    if (
      crossStageEnabled &&
      compareStage !== currentStage &&
      crossStageData.cbes.length > 0
    ) {
      const allCbes = [
        ...(filteredData as CanonicalBusinessEvent[]),
        ...(crossStageData.cbes as CanonicalBusinessEvent[]),
      ];
      return createUsecaseOptions(allCbes);
    }
    return createUsecaseOptions(filteredData as CanonicalBusinessEvent[]);
  }, [
    filteredData,
    isCbe,
    crossStageEnabled,
    compareStage,
    currentStage,
    crossStageData.cbes,
  ]);

  const productOptions = React.useMemo(() => {
    if (!isCbe || allProductIds.length <= 1) return [];
    return createProductOptions(
      filteredData as CanonicalBusinessEvent[],
      allProductIds
    );
  }, [filteredData, isCbe, allProductIds]);

  const filteredObjects = React.useMemo(() => {
    let result = filteredData as JsonDataType[];
    if (isCbe && selectedUsecase?.value && selectedDestination?.value) {
      result = filterCbesByComparisonGroup(
        filteredData as CanonicalBusinessEvent[],
        selectedUsecase,
        selectedDestination.value
      );
    }
    if (isCbe && selectedProduct?.value) {
      result = filterCbesByProduct(
        result as CanonicalBusinessEvent[],
        selectedProduct.value
      );
    }
    return result;
  }, [
    filteredData,
    isCbe,
    selectedUsecase,
    selectedDestination,
    selectedProduct,
  ]);

  const crossStageFilteredData = React.useMemo(() => {
    const data = isCbe ? crossStageData.cbes : crossStageData.events;
    let result = data as JsonDataType[];
    if (isCbe && selectedUsecase?.value && selectedDestination?.value) {
      result = filterCbesByComparisonGroup(
        data as CanonicalBusinessEvent[],
        selectedUsecase,
        selectedDestination.value
      );
    }
    if (isCbe && selectedProduct?.value) {
      result = filterCbesByProduct(
        result as CanonicalBusinessEvent[],
        selectedProduct.value
      );
    }
    return result;
  }, [
    crossStageData.cbes,
    crossStageData.events,
    isCbe,
    selectedUsecase,
    selectedDestination,
    selectedProduct,
  ]);

  const versionOptions = React.useMemo(() => {
    if (crossStageEnabled && compareStage !== currentStage) {
      return createVersionOptions(
        filteredObjects,
        selectedUsecase || undefined,
        selectedDestination?.value
      );
    }
    return createVersionOptions(
      filteredObjects,
      selectedUsecase || undefined,
      selectedDestination?.value
    );
  }, [
    filteredObjects,
    selectedUsecase,
    selectedDestination,
    crossStageEnabled,
    compareStage,
    currentStage,
  ]);

  const { leftOptions, rightOptions } = React.useMemo(() => {
    if (crossStageEnabled && compareStage !== currentStage) {
      return createCrossStageVersionOptions(
        filteredObjects,
        crossStageFilteredData,
        currentStage,
        compareStage
      );
    }
    return { leftOptions: versionOptions, rightOptions: versionOptions };
  }, [
    filteredObjects,
    crossStageFilteredData,
    currentStage,
    compareStage,
    crossStageEnabled,
    versionOptions,
  ]);

  const leftVersionOptions = leftOptions;
  const rightVersionOptions = rightOptions;

  const getDestinationOptions = React.useCallback(() => {
    if (!selectedUsecase?.value || !isCbe) return DESTINATION_OPTIONS;
    const availableDestinations = getAvailableDestinations(
      filteredData as CanonicalBusinessEvent[],
      selectedUsecase.usecaseName
    );
    return DESTINATION_OPTIONS.filter((option) =>
      availableDestinations.has(option.value || "")
    );
  }, [selectedUsecase, filteredData, isCbe]);

  const componentState = useUsecaseState(
    filteredData,
    isCbe ? crossStageData.cbes : crossStageData.events,
    crossStageEnabled,
    compareStage,
    currentStage,
    crossStageLoading
  );

  const usecaseDifferences = useUsecaseDifferences({
    crossStageEnabled,
    compareStage,
    currentStage,
    isCbe,
    isAccountingGroup,
    objects: filteredData,
    allCurrentCbes: allCbes,
    crossStageData,
    crossStageRawData,
    crossStageLoading,
  });

  const leftData = filteredObjects;
  const rightData =
    crossStageEnabled && compareStage !== currentStage
      ? crossStageFilteredData
      : filteredObjects;
  const leftItem = leftData[Number(leftSelectedVersion.value)] || null;
  const rightItem = rightData[Number(rightSelectedVersion.value)] || null;

  const diff = useComparison(
    leftItem,
    rightItem,
    crossStageEnabled,
    compareStage,
    currentStage,
    overrideId,
    identifier
  );

  const shouldShowDiff = React.useMemo(() => {
    // Don't show diff if cross-stage data is loading
    if (crossStageLoading) return false;

    // Don't show diff if no items to compare or if either side has no data
    if (!leftItem || !rightItem) return false;

    return componentState.shouldShowDiff;
  }, [leftItem, rightItem, componentState.shouldShowDiff, crossStageLoading]);

  const handleShowHideLines = () => {
    setDiffUnchangedLinesOptions(
      diffUnchangedLinesOptions === false ? DIFF_UNCHANGED_LINES_CONFIG : false
    );
  };

  const isSameVersionSelected =
    leftSelectedVersion &&
    rightSelectedVersion &&
    leftSelectedVersion.value === rightSelectedVersion.value &&
    versionOptions.length > 1 &&
    (!crossStageEnabled || compareStage === currentStage);

  const shouldHideCopyButtons = React.useMemo(() => {
    //  hide copy buttons when there's no actual data to copy
    return (
      isSameVersionSelected ||
      (versionOptions.length <= 1 &&
        (!crossStageEnabled || compareStage === currentStage)) ||
      (crossStageEnabled &&
        compareStage !== currentStage &&
        !crossStageLoading &&
        crossStageData.events.length === 0 &&
        crossStageData.cbes.length === 0)
    );
  }, [
    isSameVersionSelected,
    versionOptions.length,
    crossStageEnabled,
    compareStage,
    currentStage,
    crossStageLoading,
    crossStageData.events.length,
    crossStageData.cbes.length,
  ]);

  React.useEffect(() => {
    if (isCbe && usecaseOptions.length > 0 && !selectedUsecase) {
      const restoredUsecase = initialUsecaseValue.current
        ? usecaseOptions.find(
            (option) => option.value === initialUsecaseValue.current
          )
        : undefined;
      initialUsecaseValue.current = null;
      setSelectedUsecase(restoredUsecase || usecaseOptions[0]);
    } else if (!isCbe && selectedUsecase) {
      setSelectedUsecase(null);
    } else if (
      selectedUsecase &&
      usecaseOptions.length > 0 &&
      !usecaseOptions.some((option) => option.value === selectedUsecase.value)
    ) {
      setSelectedUsecase(usecaseOptions[0]);
    }
  }, [isCbe, usecaseOptions, selectedUsecase]);

  React.useEffect(() => {
    if (productOptions.length > 0 && !selectedProduct) {
      setSelectedProduct(productOptions[0]);
    } else if (productOptions.length === 0 && selectedProduct) {
      setSelectedProduct(null);
    }
  }, [productOptions, selectedProduct]);

  React.useEffect(() => {
    if (crossStageEnabled && (isDevo(compareStage) || isDevo(currentStage))) {
      // Beta data from ARK can sometimes include fake override IDs from redrives, which breaks comparisons
      const baseId = identifier.split("#")[0];
      setOverrideId(baseId);
    } else if (crossStageEnabled) {
      setOverrideId(identifier);
    } else {
      setOverrideId("");
    }
  }, [crossStageEnabled, compareStage, currentStage, identifier]);

  React.useEffect(() => {
    if (selectedUsecase?.value && isCbe) {
      const availableDestinations = getDestinationOptions();
      if (
        selectedDestination &&
        !availableDestinations.some(
          (opt) => opt.value === selectedDestination.value
        )
      ) {
        if (availableDestinations.length > 0)
          setSelectedDestination(availableDestinations[0]);
      }
    }
  }, [selectedUsecase, isCbe, getDestinationOptions, selectedDestination]);

  React.useEffect(() => {
    let leftRestored = false;
    let rightRestored = false;
    if (hasUrlVersions.current) {
      const urlLeft = initialParams.get("cmpLeft");
      const urlRight = initialParams.get("cmpRight");
      const allOpts =
        crossStageEnabled && compareStage !== currentStage
          ? leftVersionOptions
          : versionOptions;
      const rightOpts =
        crossStageEnabled && compareStage !== currentStage
          ? rightVersionOptions
          : versionOptions;
      if (
        (urlLeft && allOpts.length === 0) ||
        (urlRight && rightOpts.length === 0)
      )
        return;
      hasUrlVersions.current = false;
      if (urlLeft) {
        const match = allOpts.find((o) => o.label === urlLeft);
        if (match) {
          setLeftSelectedVersion(match);
          leftRestored = true;
        }
      }
      if (urlRight) {
        const match = rightOpts.find((o) => o.label === urlRight);
        if (match) {
          setRightSelectedVersion(match);
          rightRestored = true;
        }
      }
      if (leftRestored && rightRestored) return;
      // Fall through — guard default logic per side below
    }
    if (crossStageEnabled && compareStage !== currentStage) {
      if (leftVersionOptions.length > 0 && !leftRestored)
        setLeftSelectedVersion(
          leftVersionOptions[leftVersionOptions.length - 1]
        );
      if (rightVersionOptions.length > 0 && !rightRestored)
        setRightSelectedVersion(
          rightVersionOptions[rightVersionOptions.length - 1]
        );
    } else if (versionOptions.length > 0) {
      if (!rightRestored)
        setRightSelectedVersion(versionOptions[versionOptions.length - 1]);
      if (!leftRestored)
        setLeftSelectedVersion(
          versionOptions.length > 1
            ? versionOptions[versionOptions.length - 2]
            : versionOptions[versionOptions.length - 1]
        );
    }
  }, [
    versionOptions,
    leftVersionOptions,
    rightVersionOptions,
    crossStageEnabled,
    compareStage,
    currentStage,
  ]);

  React.useEffect(() => {
    if (!isModalVisible) return;
    syncCompareParams({
      cmpLeft:
        leftSelectedVersion.label && leftSelectedVersion.label !== "No version"
          ? leftSelectedVersion.label.toString()
          : null,
      cmpRight:
        rightSelectedVersion.label &&
        rightSelectedVersion.label !== "No version"
          ? rightSelectedVersion.label.toString()
          : null,
      cmpUsecase: selectedUsecase?.value || null,
      cmpDest: selectedDestination?.value || null,
      cmpProduct: selectedProduct?.value || null,
      cmpStage: compareStage !== currentStage ? compareStage : null,
    });
  }, [
    isModalVisible,
    leftSelectedVersion.label,
    rightSelectedVersion.label,
    selectedUsecase?.value,
    selectedDestination?.value,
    selectedProduct?.value,
    compareStage,
    currentStage,
    syncCompareParams,
  ]);

  if (!isModalVisible) return null;

  return (
    <Suspense fallback={<div>Loading content...</div>}>
      <Container>
        <Grid
          gridDefinition={[{ colspan: 12 }, { colspan: 12 }, { colspan: 12 }]}
        >
          {componentState.shouldShowUsecaseDropdown && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <UsecaseDestinationSelects
                selectedUsecase={selectedUsecase}
                setSelectedUsecase={setSelectedUsecase}
                selectedDestination={selectedDestination}
                setSelectedDestination={setSelectedDestination}
                validUsecases={usecaseOptions}
                destinationOptions={getDestinationOptions()}
                dropdownDisabled={componentState.dropdownDisabled}
                productOptions={productOptions}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
              />
            </div>
          )}

          <VersionSelectionControls
            versionState={{
              leftSelectedVersion,
              rightSelectedVersion,
              leftVersionOptions,
              rightVersionOptions,
              versionOptions,
            }}
            versionHandlers={{
              setLeftSelectedVersion,
              setRightSelectedVersion,
            }}
            stageConfig={{
              currentStage,
              compareStage,
              setCompareStage,
              stageOptions: STAGE_OPTIONS,
              crossStageEnabled,
              crossStageLoading,
            }}
            overrideConfig={{
              setOverrideId,
              identifier,
            }}
            dataContext={{
              filteredObjects,
              crossStageData,
              crossStageFilteredData,
              isCbe,
              isAccountingGroup,
              selectedUsecase,
              selectedDestination,
            }}
            handleShowHideLines={handleShowHideLines}
            diffUnchangedLinesOptions={diffUnchangedLinesOptions}
            shouldHideCopyButtons={shouldHideCopyButtons}
            shouldShowDiff={shouldShowDiff}
          />

          <SpaceBetween size="m">
            {usecaseDifferences && (
              <UsecaseDifferencesAlert
                usecaseDifferences={usecaseDifferences}
                currentStage={currentStage}
                compareStage={compareStage}
              />
            )}
            {versionOptions.length <= 1 &&
              (!crossStageEnabled || compareStage === currentStage) && (
                <Alert>
                  {isCbe && filteredData.length === 0
                    ? "No meaningful CBE data available to compare (only NoOp CBEs found)."
                    : "Only one version exists, nothing to compare."}
                </Alert>
              )}
            {crossStageEnabled &&
              compareStage !== currentStage &&
              !crossStageLoading &&
              crossStageData.events.length === 0 &&
              crossStageData.cbes.length === 0 && (
                <Alert type="info">
                  {crossStageRawData.totalEvents === 0 &&
                  crossStageRawData.totalCbes === 0
                    ? `No data available in ${compareStage}.`
                    : `No data available in ${compareStage} for the current flow type.`}
                </Alert>
              )}
            {isSameVersionSelected && (
              <Alert type="warning">
                Same versions selected for comparison - nothing to compare.
              </Alert>
            )}
            {crossStageEnabled &&
              compareStage !== currentStage &&
              crossStageLoading && (
                <Alert type="info">Loading data from {compareStage}...</Alert>
              )}
            {shouldShowDiff && (
              <DiffDisplayGrid
                diff={diff}
                versionOptions={versionOptions}
                crossStageEnabled={crossStageEnabled}
                compareStage={compareStage}
                currentStage={currentStage}
                isSameVersionSelected={isSameVersionSelected}
                diffUnchangedLinesOptions={diffUnchangedLinesOptions}
              />
            )}
          </SpaceBetween>
        </Grid>
      </Container>
    </Suspense>
  );
};

export default JsonCompareComponent;
